import React, { useEffect, useState } from "react";
import { apiCall } from "utils/httpClient";
import { successToast, errorToast } from "utils/httpClient";
import apiEndPoints from "utils/apiEndPoints";
import { handleDetailResponse } from "utils/utils";

// JUSTO-64: GRE allocation modal – allocate visitor to Closing Manager or SCM (dual role). List = CMs + SCM only; no SM list.
const AllocateSM = ({
  show,
  propertyId,
  leadId,
  handleClose,
  onAllocateSuccess,
}) => {
  const [managerList, setManagerList] = useState([]); // JUSTO-64: CM + SCM from GET_PROPERTY_BASED_CM only (no SM)
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Filtered list based on search query
  const filteredList = managerList.filter(
    (item) =>
      item?.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item?.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item?.mobile?.includes(searchQuery),
  );

  // JUSTO-64: Fetch CMs + SCM only (backend GET_PROPERTY_BASED_CM returns both; no SM fetch).
  useEffect(() => {
    const fetchCMs = async () => {
      if (show && propertyId) {
        setLoading(true);
        try {
          const cmResponse = await apiCall(
            "post",
            apiEndPoints.GET_PROPERTY_BASED_CM,
            {
              property_id: propertyId,
            },
          );

          const cmData =
            cmResponse?.data?.status === 200 ? cmResponse.data.data || [] : [];
          setManagerList(cmData);

          if (
            cmResponse?.data?.status !== 200 &&
            cmResponse?.data?.status !== 201
          ) {
            errorToast(
              cmResponse?.data?.message || "Failed to fetch Closing Managers",
            );
          }
        } catch (error) {
          console.error("Error fetching Closing Managers list:", error);
          errorToast("Failed to fetch Closing Managers");
          setManagerList([]);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchCMs();
  }, [show, propertyId]);

  // Handle selection (CM or SCM dual role)
  const handleSelection = (item) => {
    setSelectedPerson(item);
  };

  // Handle "Allocate" click
  const handleAllocate = async () => {
    if (!selectedPerson) {
      errorToast("Please select a Closing Manager");
      return;
    }

    if (!leadId) {
      errorToast("Lead ID is missing");
      return;
    }

    setLoading(true);
    try {
      // First, fetch visitor details to get required fields
      const visitorDetailResponse = await apiCall(
        "post",
        apiEndPoints.VISITORDETAIL,
        {
          lead_id: leadId,
        },
      );

      if (visitorDetailResponse?.data?.status !== 200) {
        errorToast("Failed to fetch visitor details");
        setLoading(false);
        return;
      }

      const visitorData = handleDetailResponse(
        visitorDetailResponse?.data?.data || {},
      );

      // Extract customer data from nested structure (customer_detail contains customer info)
      const customerData = visitorData?.customer_detail || {};
      const first_name =
        customerData?.first_name || visitorData?.first_name || "";
      const last_name = customerData?.last_name || visitorData?.last_name || "";
      const mobile = customerData?.mobile || visitorData?.mobile || "";
      const email = customerData?.email || visitorData?.email || "";
      const property_id = visitorData?.property_id || propertyId || "";

      // Validate required fields
      if (!first_name || !mobile || !leadId) {
        console.error("Missing required fields:", {
          first_name,
          mobile,
          leadId,
          visitorData,
        });
        errorToast("Visitor data is incomplete. Please try again.");
        setLoading(false);
        return;
      }

      // Validate that lead exists and has required structure
      if (!visitorData || !visitorData._id) {
        console.error("Invalid visitor data structure:", visitorData);
        errorToast("Invalid visitor data. Please try again.");
        setLoading(false);
        return;
      }

      // Validate that lead has create_by field (required by backend)
      if (!visitorData.create_by) {
        console.error("Lead missing create_by field:", visitorData);
        errorToast("Lead data is incomplete. Cannot allocate.");
        setLoading(false);
        return;
      }

      // JUSTO-64: All entries are CM or SCM – always send for_cm_id (no for_sm_id).
      const formData = new FormData();
      formData.append("lead_id", leadId);
      formData.append("for_cm_id", selectedPerson._id);
      formData.append("first_name", first_name);
      formData.append("mobile", mobile);

      if (property_id) {
        formData.append("property_id", property_id);
      }

      const formDataObj = {};
      for (const [key, value] of formData.entries()) {
        formDataObj[key] = value;
      }

      const urlParams = new URLSearchParams(formDataObj).toString();

      const response = await apiCall(
        "post",
        apiEndPoints.EDITVISITOR,
        urlParams,
        {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      );

      if (response?.data?.status === 200) {
        successToast(
          response?.data?.message || "Visitor allocated successfully",
        );
        if (onAllocateSuccess) {
          onAllocateSuccess(selectedPerson);
        }
        handleClose();
      } else {
        console.error("🔵 AllocateSM - Backend error:", response?.data);
        errorToast(response?.data?.message || "Failed to allocate visitor");
      }
    } catch (error) {
      console.error("Error allocating visitor:", error);
      errorToast(
        "Failed to allocate visitor. Please check console for details.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Return null if modal is not shown
  if (!show) return null;

  return (
    <>
      {/* Backdrop with blur effect */}
      <div
        className="modal-backdrop"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(5px)",
          zIndex: 1040,
        }}
      ></div>

      {/* Modal Dialog */}
      <div
        className="modal show"
        style={{ display: "block", zIndex: 1050 }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content" style={{ height: "80vh" }}>
            <div className="modal-header">
              <h5 className="modal-title">
                Allocate Visitor to Closing Manager
              </h5>
              <button type="button" className="close" onClick={handleClose}>
                <span>&times;</span>
              </button>
            </div>
            <div
              className="modal-body"
              style={{ maxHeight: "70vh", overflowY: "auto" }}
            >
              {/* Display selected CM or SCM (dual role) */}
              {selectedPerson && (
                <div
                  style={{
                    border: "1px solid #007bff",
                    padding: "10px",
                    marginBottom: "20px",
                    backgroundColor: "#e7f1ff",
                    borderRadius: "4px",
                  }}
                >
                  <strong>
                    Selected:{" "}
                    {selectedPerson.role === "SCM"
                      ? "SCM (Dual role)"
                      : "Closing Manager"}
                  </strong>
                  <div style={{ marginTop: "5px" }}>
                    <div>
                      <strong>Name:</strong> {selectedPerson.user_name}
                    </div>
                    {/*<div>
                      <strong>Email:</strong> {selectedPerson.email}
                    </div>*/}
                  </div>
                </div>
              )}

              {/* Search input */}
              <div style={{ marginBottom: "20px" }}>
                <input
                  type="text"
                  placeholder="Search by Name, Email or Mobile"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    padding: "8px",
                    width: "100%",
                    boxSizing: "border-box",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                  }}
                />
              </div>

              {/* Loading state */}
              {loading && managerList.length === 0 && (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  Loading Closing Managers...
                </div>
              )}

              {/* No one found */}
              {!loading && managerList.length === 0 && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "#666",
                  }}
                >
                  No Closing Managers found for this property.
                </div>
              )}

              {/* List of CM + SCM (dual role) with radio buttons */}
              {!loading && managerList.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  {filteredList.length === 0 ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "20px",
                        color: "#666",
                      }}
                    >
                      No one matches your search.
                    </div>
                  ) : (
                    filteredList.map((item, index) => {
                      const isSelected = selectedPerson?._id === item._id;
                      const roleLabel = item.role === "SCM" ? "SCM" : "CM";
                      return (
                        <label
                          key={`${item._id}-${item.role || "cm"}-${index}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "10px",
                            border: isSelected
                              ? "2px solid #007bff"
                              : "1px solid #ddd",
                            borderRadius: "4px",
                            cursor: "pointer",
                            backgroundColor: isSelected ? "#f0f8ff" : "#fff",
                          }}
                        >
                          <input
                            type="radio"
                            name="selectedPerson"
                            checked={isSelected}
                            onChange={() => handleSelection(item)}
                            style={{ marginRight: "10px" }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: "bold" }}>
                              [{roleLabel}] {item.user_name}
                            </div>
                            {/*<div style={{ fontSize: "0.9em", color: "#666" }}>
                              {item.email} | {item.mobile}
                            </div>*/}
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
              )}
            </div>
            <div
              className="modal-footer"
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
                disabled={loading}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAllocate}
                disabled={loading || !selectedPerson}
                style={{
                  position: "absolute",
                  left: "50%",
                  transform: "translateX(-50%)",
                  margin: 0,
                }}
              >
                {loading ? "Allocating..." : "Allocate"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AllocateSM;
